package com.eladfinish.bible.contest.screens.lexicographicalSort

import com.eladfinish.bible.contest.analytics.AnalyticsConstants.LexicographicalSort
import com.eladfinish.bible.contest.analytics.providers.AnalyticsProvider
import com.eladfinish.bible.contest.arch.ViewModelStore
import com.eladfinish.bible.contest.constants.Constants
import com.eladfinish.bible.contest.domain.BooksRepository
import com.eladfinish.bible.contest.screens.browse.tableOfContents.model.OutputChapters
import com.russhwolf.settings.Settings
import kotlinx.collections.immutable.toImmutableList
import kotlinx.coroutines.flow.MutableStateFlow

class LexicographicalSortStore(
    private val params: LexicographicalSortStoreParams,
    private val logicUseCase: LexicographicalSortLogicUseCase,
    private val getVerseCountUseCase: GetVerseCountUseCase,
    private val analyticsProvider: AnalyticsProvider,
    private val settings: Settings,
) : ViewModelStore<LexicographicalSortState, LexicographicalSortAction, LexicographicalSortEffect>(
    tag = "LexicographicalSortStore",
) {

    companion object {
        data class LexicographicalSortStoreParams(
            val bookIndex: Int = -1,
            val chapterIndex: Int = -1,
        )
    }

    private val isPremium: Boolean
        get() = settings.getBoolean(key = Constants.IS_PREMIUM, defaultValue = false)

    override val stateFlow = MutableStateFlow(initialState())

    init {
        analyticsProvider.logEvent(
            LexicographicalSort.EVENT_OPENED,
            mapOf(
                LexicographicalSort.PARAM_SOURCE to
                        if (logicUseCase.isRequestedSelectionValid(params.bookIndex, params.chapterIndex)) {
                            LexicographicalSort.SOURCE_BROWSE
                        } else {
                            LexicographicalSort.SOURCE_OTHER
                        },
            ),
        )
        recomputeVerseCount(
            bookNumber = currentState.selectedBookIndex,
            chapterNumber = currentState.selectedChapterIndex,
            outputChapters = currentState.selectedOutputChapters,
        )
    }

    private fun initialState(): LexicographicalSortState {
        val bookNames = BooksRepository.bookNamesHebrew.toImmutableList()
        val initialSelection = logicUseCase.initialSelection(
            requestedBookIndex = params.bookIndex,
            requestedChapterIndex = params.chapterIndex,
            savedBookIndex = settings.getInt(key = Constants.PREF_LEXICOGRAPHICAL_SORT_BOOK_INDEX, defaultValue = -1),
            savedChapterIndex = settings.getInt(key = Constants.PREF_LEXICOGRAPHICAL_SORT_CHAPTER_INDEX, defaultValue = -1),
        )

        persistSelection(bookIndex = initialSelection.bookIndex, chapterIndex = initialSelection.chapterIndex)

        return LexicographicalSortState(
            bookNames = bookNames,
            selectedBookIndex = initialSelection.bookIndex,
            selectedChapterIndex = initialSelection.chapterIndex,
            numberOfChaptersInSelectedBook = initialSelection.numberOfChaptersInBook,
            selectedOutputChapters = OutputChapters.SINGLE,
            versesInTable = 0,
            dialogState = LexicographicalSortState.DialogState.None,
        )
    }

    /**
     * Recomputes [LexicographicalSortState.versesInTable] for the given selection off the
     * main path and updates the state when done. Takes the selection explicitly because it
     * is called from the reducers before the new state has been emitted.
     */
    private fun recomputeVerseCount(
        bookNumber: Int,
        chapterNumber: Int,
        outputChapters: OutputChapters,
    ) {
        launchEffect {
            val count = getVerseCountUseCase(
                bookNumber = bookNumber,
                chapterNumber = chapterNumber,
                outputChapters = outputChapters,
            )
            updateState { it.copy(versesInTable = count) }
        }
    }

    private fun persistSelection(bookIndex: Int, chapterIndex: Int) {
        settings.putInt(Constants.PREF_LEXICOGRAPHICAL_SORT_BOOK_INDEX, bookIndex)
        settings.putInt(Constants.PREF_LEXICOGRAPHICAL_SORT_CHAPTER_INDEX, chapterIndex)
    }

    override fun handleAction(action: LexicographicalSortAction): LexicographicalSortState =
        when (action) {
            is LexicographicalSortAction.BookSelected -> handleBookSelected(action.bookIndex)
            is LexicographicalSortAction.ChapterSelected -> handleChapterSelected(action.chapterIndex)
            is LexicographicalSortAction.OutputChaptersSelected -> handleOutputChaptersSelected(action.outputChapters)
            LexicographicalSortAction.GenerateClicked -> handleGenerateClicked()
            LexicographicalSortAction.DismissDialog -> currentState.copy(dialogState = LexicographicalSortState.DialogState.None)
        }

    private fun handleBookSelected(bookIndex: Int): LexicographicalSortState {
        val selection = logicUseCase.selectBook(
            bookIndex = bookIndex,
            currentChapterIndex = currentState.selectedChapterIndex,
        ) ?: return currentState

        persistSelection(bookIndex = selection.bookIndex, chapterIndex = selection.chapterIndex)

        recomputeVerseCount(
            bookNumber = selection.bookIndex,
            chapterNumber = selection.chapterIndex,
            outputChapters = currentState.selectedOutputChapters,
        )

        return currentState.copy(
            selectedBookIndex = selection.bookIndex,
            numberOfChaptersInSelectedBook = selection.numberOfChaptersInBook,
            selectedChapterIndex = selection.chapterIndex,
        )
    }

    private fun handleChapterSelected(chapterIndex: Int): LexicographicalSortState {
        if (!logicUseCase.isChapterSelectionValid(
                chapterIndex = chapterIndex,
                numberOfChaptersInSelectedBook = currentState.numberOfChaptersInSelectedBook,
            )
        ) return currentState

        persistSelection(bookIndex = currentState.selectedBookIndex, chapterIndex = chapterIndex)

        recomputeVerseCount(
            bookNumber = currentState.selectedBookIndex,
            chapterNumber = chapterIndex,
            outputChapters = currentState.selectedOutputChapters,
        )

        return currentState.copy(selectedChapterIndex = chapterIndex)
    }

    private fun handleOutputChaptersSelected(outputChapters: OutputChapters): LexicographicalSortState {
        if (logicUseCase.requiresPremiumUpsell(outputChapters = outputChapters, isPremium = isPremium)) {
            return currentState.copy(
                dialogState = LexicographicalSortState.DialogState.PremiumRequired(
                    message = "מיון לקסיקוגרפי של כל פרקי הספר זמין רק במנוי הפרימיום."
                )
            )
        }

        recomputeVerseCount(
            bookNumber = currentState.selectedBookIndex,
            chapterNumber = currentState.selectedChapterIndex,
            outputChapters = outputChapters,
        )

        return currentState.copy(selectedOutputChapters = outputChapters)
    }

    private fun handleGenerateClicked(): LexicographicalSortState {
        analyticsProvider.logEvent(
            LexicographicalSort.EVENT_GENERATE,
            lexicographicalSortParams(
                outputChapters = currentState.selectedOutputChapters,
                bookNumber = currentState.selectedBookIndex,
                chapterNumber = currentState.selectedChapterIndex,
            ),
        )
        launchEffect {
            sideEffectFlow.emit(
                LexicographicalSortEffect.ShowToastStringResource(
                    currentState.selectedOutputChapters.sentence,
                    currentState.selectedOutputChapters.snackbarDuration,
                )
            )
            sideEffectFlow.emit(
                LexicographicalSortEffect.NavigateToPdfViewer(
                    bookIndex = currentState.selectedBookIndex,
                    chapterIndex = currentState.selectedChapterIndex,
                    outputChaptersOrdinal = currentState.selectedOutputChapters.ordinal,
                )
            )
        }
        return currentState
    }
}
